import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { analytics } from "@shared/utils/analytics";
import styles from "./button.module.scss";

export type ButtonVariant = "default" | "secondary";
export type ButtonSize = "default" | "short" | "icon";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  analyticsName?: string; // Имя для аналитики, если не указано - будет использовано children или "Button"
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", asChild = false, analyticsName, onClick, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        // Логируем клик
        const buttonName =
          analyticsName ||
          (typeof children === "string" ? children : "Button") ||
          "Button";
        
        // Логируем в консоль с подробным описанием
        const buttonDescription = `[Button Click] "${buttonName}" (variant: ${variant || "default"}, size: ${size || "default"}, disabled: ${props.disabled || false})`;
        console.log(buttonDescription);
        
        analytics.logButtonClick(buttonName, {
          variant,
          size,
          disabled: props.disabled
        });

        // Вызываем оригинальный onClick
        onClick?.(e);
      },
      [analyticsName, children, variant, size, props.disabled, onClick]
    );

    const buttonClasses = [
      styles.button,
      variant === "default" ? styles.buttonDefault : styles.buttonSecondary,
      size === "default" ? styles.buttonSizeDefault : 
      size === "short" ? styles.buttonSizeShort : 
      styles.buttonSizeIcon,
      className
    ].filter(Boolean).join(" ");

    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={buttonClasses}
        onClick={handleClick}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button };
